import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit, ChevronUp, ChevronDown, GripVertical,
  Save, X, Play, RotateCcw, Award,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import TournamentIOToolbar from "@/components/director/TournamentIOToolbar";

import { API } from '../../utils/apiBase';

/**
 * Manages the ordered list of 1v1 battles for a `1vs1_battles` tournament.
 * - Add battle (auto-numbered)
 * - Inline edit + delete (blocked when the battle is in progress)
 * - Drag & drop reorder + up/down arrows
 * - CSV/XLSX template export + bulk import
 */
export default function BattlesListManager({
  tournament,
  athletes,
  onReload,
  startBattle,
  resetBattle,
  currentBattleId,
  currentBattleStatus,
}) {
  const { t } = useI18n();
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newA1, setNewA1] = useState("");
  const [newA2, setNewA2] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editA1, setEditA1] = useState("");
  const [editA2, setEditA2] = useState("");
  const [dragIdx, setDragIdx] = useState(null);

  const load = useCallback(async () => {
    if (!tournament?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/battles-list/${tournament.id}`);
      setBattles(r.data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [tournament?.id]);

  useEffect(() => { load(); }, [load, currentBattleId, currentBattleStatus]);

  // Category-filtered pool
  const pool = (athletes || []).filter((a) => {
    if (!a) return false;
    const cat = tournament?.category || "ALL";
    if (cat === "ALL") return true;
    if (cat === "Male") return a.gender === "Male";
    if (cat === "Female") return a.gender === "Female";
    if (cat === "NextGen") return a.nextgen === true;
    return true;
  });

  const findAthlete = (id) => athletes.find((a) => a?.id === id);
  const isLocked = (b) => b.status && b.status !== "pending";
  const isDone = (b) => b.status === "completed";
  const liveBattleExists = battles.some(isLocked) || !!currentBattleId;

  const handleReset = async (b) => {
    await resetBattle(b.id);
    await load();
  };

  const addBattle = async () => {
    if (!newA1 || !newA2) { toast.error(t("battlesList.bothRequired")); return; }
    if (newA1 === newA2) { toast.error(t("battlesList.sameAthlete")); return; }
    try {
      await axios.post(`${API}/battles-list/${tournament.id}`, {
        athlete1_id: newA1,
        athlete2_id: newA2,
      });
      toast.success(t("battlesList.added"));
      setNewA1(""); setNewA2("");
      await load();
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditA1(b.athlete1_id || "");
    setEditA2(b.athlete2_id || "");
  };

  const saveEdit = async (b) => {
    if (!editA1 || !editA2) { toast.error(t("battlesList.bothRequired")); return; }
    if (editA1 === editA2) { toast.error(t("battlesList.sameAthlete")); return; }
    try {
      await axios.put(`${API}/battles-list/${b.id}`, {
        athlete1_id: editA1,
        athlete2_id: editA2,
      });
      toast.success(t("battlesList.updated"));
      setEditingId(null);
      await load();
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const removeBattle = async (b) => {
    if (!window.confirm(t("battlesList.confirmDelete").replace("{n}", b.match_number))) return;
    try {
      await axios.delete(`${API}/battles-list/${b.id}`);
      toast.success(t("battlesList.deleted"));
      await load();
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const applyReorder = async (newOrderIds) => {
    try {
      await axios.put(`${API}/battles-list/${tournament.id}/reorder`, { battle_ids: newOrderIds });
      await load();
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const moveBattle = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= battles.length) return;
    if (liveBattleExists) {
      toast.error(t("battlesList.reorderBlocked"));
      return;
    }
    const newOrder = [...battles];
    const [moved] = newOrder.splice(idx, 1);
    newOrder.splice(target, 0, moved);
    applyReorder(newOrder.map((b) => b.id));
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    if (liveBattleExists) {
      toast.error(t("battlesList.reorderBlocked"));
      setDragIdx(null); return;
    }
    const newOrder = [...battles];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, moved);
    setDragIdx(null);
    applyReorder(newOrder.map((b) => b.id));
  };

  return (
    <Card className="p-6 glass mb-6" data-testid="battles-list-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-2xl font-bold text-foreground">{t("battlesList.title")}</h3>
        <TournamentIOToolbar
          templateUrl={`${API}/battles-list/${tournament.id}/template`}
          importUrl={`${API}/battles-list/${tournament.id}/import`}
          exportUrl={`${API}/tournament/${tournament.id}/battles/export`}
          testIdPrefix="battles-list-io"
          importParams={window.confirm ? "" : ""}
          onImported={() => {
            load();
            onReload && onReload();
          }}
        />
      </div>

      {/* Add row */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 mb-4">
        <select
          value={newA1}
          onChange={(e) => setNewA1(e.target.value)}
          className="bg-muted text-foreground border border-border rounded px-3 py-2"
          data-testid="battles-list-new-a1"
        >
          <option value="">{t("battlesList.selectA1")}</option>
          {pool.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}{a.fid ? ` · ${a.fid}` : ""}{a.country ? ` · ${a.country}` : ""}
            </option>
          ))}
        </select>
        <select
          value={newA2}
          onChange={(e) => setNewA2(e.target.value)}
          className="bg-muted text-foreground border border-border rounded px-3 py-2"
          data-testid="battles-list-new-a2"
        >
          <option value="">{t("battlesList.selectA2")}</option>
          {pool.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}{a.fid ? ` · ${a.fid}` : ""}{a.country ? ` · ${a.country}` : ""}
            </option>
          ))}
        </select>
        <Button onClick={addBattle} disabled={!newA1 || !newA2} data-testid="battles-list-add-btn">
          <Plus size={16} className="mr-2" /> {t("battlesList.addBattle")}
        </Button>
      </div>

      {/* List */}
      {loading && battles.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
      ) : battles.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="battles-list-empty">
          {t("battlesList.empty")}
        </p>
      ) : (
        <div className="space-y-2">
          {battles.map((b, idx) => {
            const a1 = findAthlete(b.athlete1_id);
            const a2 = findAthlete(b.athlete2_id);
            const live = isLocked(b);
            const done = isDone(b);
            const winner = done ? findAthlete(b.winner_id) : null;
            const isEditing = editingId === b.id;
            return (
              <div
                key={b.id}
                draggable={!live}
                onDragStart={() => !live && handleDragStart(idx)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(idx)}
                className={`flex flex-wrap items-center gap-2 p-3 rounded-lg border ${
                  dragIdx === idx ? "border-primary bg-primary/10" : "border-border bg-muted"
                } ${live ? "opacity-90" : ""}`}
                data-testid={`battles-list-row-${b.match_number}`}
              >
                {!live && <GripVertical size={16} className="text-muted-foreground cursor-grab" />}
                <span className="font-bold text-primary text-base shrink-0 w-12 text-center">
                  B{b.match_number}
                </span>

                {isEditing ? (
                  <>
                    <select value={editA1} onChange={(e) => setEditA1(e.target.value)} className="bg-background text-foreground border border-border rounded px-2 py-1 text-sm flex-1 min-w-[150px]">
                      <option value="">{t("battlesList.selectA1")}</option>
                      {pool.map((a) => (<option key={a.id} value={a.id}>{a.name}{a.fid ? ` · ${a.fid}` : ""}</option>))}
                    </select>
                    <span className="text-muted-foreground">vs</span>
                    <select value={editA2} onChange={(e) => setEditA2(e.target.value)} className="bg-background text-foreground border border-border rounded px-2 py-1 text-sm flex-1 min-w-[150px]">
                      <option value="">{t("battlesList.selectA2")}</option>
                      {pool.map((a) => (<option key={a.id} value={a.id}>{a.name}{a.fid ? ` · ${a.fid}` : ""}</option>))}
                    </select>
                    <Button size="sm" onClick={() => saveEdit(b)} data-testid={`battles-list-save-${b.match_number}`}>
                      <Save size={14} className="mr-1" /> {t("common.save")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                      <X size={14} />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 text-foreground text-sm truncate">
                      {(a1?.name || "TBD")} <span className="text-muted-foreground">vs</span> {(a2?.name || "TBD")}
                    </span>
                    {live && !done && (
                      <span className="text-xs text-orange-500 font-semibold shrink-0" data-testid={`battles-list-status-${b.match_number}`}>
                        {t("battlesList.inProgress")}
                      </span>
                    )}
                    {done && (
                      <span className="text-xs text-green-500 font-semibold shrink-0 flex items-center gap-1" data-testid={`battles-list-result-${b.match_number}`}>
                        <Award size={14} />
                        {t("battlesList.completed")} · {b.winner_id === "BYE" ? "BYE" : (winner?.name || "—")}
                        {b.athlete1_total_score != null && ` (${b.athlete1_total_score} - ${b.athlete2_total_score})`}
                      </span>
                    )}
                    {live && resetBattle && (
                      <Button size="sm" variant="outline" className="border-orange-500/50 text-orange-500 hover:bg-orange-500/20" onClick={() => handleReset(b)} data-testid={`battles-list-reset-${b.match_number}`}>
                        <RotateCcw size={14} className="mr-1" /> Reset
                      </Button>
                    )}
                    {!live && startBattle && a1 && a2 && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => startBattle(b.id)} data-testid={`battles-list-start-${b.match_number}`}>
                        <Play size={14} className="mr-1" /> Start
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" disabled={live || idx === 0} onClick={() => moveBattle(idx, -1)} title={t("battlesList.moveUp")} data-testid={`battles-list-up-${b.match_number}`}>
                      <ChevronUp size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" disabled={live || idx === battles.length - 1} onClick={() => moveBattle(idx, 1)} title={t("battlesList.moveDown")} data-testid={`battles-list-down-${b.match_number}`}>
                      <ChevronDown size={16} />
                    </Button>
                    <Button size="sm" variant="outline" disabled={live} onClick={() => startEdit(b)} data-testid={`battles-list-edit-${b.match_number}`}>
                      <Edit size={14} />
                    </Button>
                    <Button size="sm" variant="destructive" disabled={live} onClick={() => removeBattle(b)} data-testid={`battles-list-delete-${b.match_number}`}>
                      <Trash2 size={14} />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
